import { keyframes } from "@emotion/react";
import { useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
const SLASH_ANIMATION_MS = 300;
const slashDrawIn = keyframes `
  from { transform: translate(-50%, -50%) rotate(-45deg) scaleX(0); }
  to   { transform: translate(-50%, -50%) rotate(-45deg) scaleX(1); }
`;
const slashDrawOut = keyframes `
  from { transform: translate(-50%, -50%) rotate(-45deg) scaleX(1); }
  to   { transform: translate(-50%, -50%) rotate(-45deg) scaleX(0); }
`;
export const useSkipDaySlashAnimation = (isMutating, isSkipDay, isInCurrentMonth) => {
    const theme = useTheme();
    // Declared first so synchronous ref logic below can reference it.
    const [animationPhase, setAnimationPhase] = useState("idle");
    // Capture the skip state at the moment mutation starts so we know direction.
    // true = was a skip day (user is unskipping) → green spinner
    // false = was not a skip day (user is skipping) → amber spinner
    const wasSkipRef = useRef(false);
    const prevMutatingRef = useRef(false);
    const pendingAnimationRef = useRef(false);
    if (isMutating && !prevMutatingRef.current) {
        wasSkipRef.current = !!isSkipDay;
    }
    if (!isMutating && prevMutatingRef.current) {
        // Mutation just completed; the animation-phase effect hasn't fired yet.
        // Suppress the slash for this one render to prevent a full-width flash.
        pendingAnimationRef.current = true;
    }
    if (animationPhase !== "idle") {
        pendingAnimationRef.current = false;
    }
    prevMutatingRef.current = isMutating;
    const spinnerColor = wasSkipRef.current ? theme.palette.success.main : theme.palette.warning.main;
    // Read-only ref so the effect can access the latest isSkipDay without
    // adding it to the dependency array (which would cause spurious re-firings
    // on SWR revalidations unrelated to the mutation lifecycle).
    const isSkipDayRef = useRef(isSkipDay);
    isSkipDayRef.current = isSkipDay;
    const prevMutatingEffectRef = useRef(false);
    useEffect(() => {
        if (!isMutating && prevMutatingEffectRef.current) {
            const expectedPostMutationState = !wasSkipRef.current;
            if (!!isSkipDayRef.current === expectedPostMutationState) {
                // Mutation succeeded — isSkipDay changed in the expected direction.
                setAnimationPhase(wasSkipRef.current ? "draw-out" : "draw-in");
            }
            else {
                // Mutation failed — isSkipDay did not change. Clear the pending flag
                // so the day snaps back to its pre-click visual state with no animation.
                pendingAnimationRef.current = false;
            }
        }
        prevMutatingEffectRef.current = isMutating;
    }, [isMutating]);
    useEffect(() => {
        if (animationPhase === "idle")
            return;
        const timer = setTimeout(() => setAnimationPhase("idle"), SLASH_ANIMATION_MS);
        return () => clearTimeout(timer);
    }, [animationPhase]);
    // Only suppress the slash while waiting for the animation-phase effect to
    // fire AND the mutation actually changed the skip state in the expected
    // direction. If the mutation failed (isSkipDay didn't change direction),
    // don't suppress — the day must snap back to its pre-click visual state.
    const mutationChangedState = !!isSkipDay === !wasSkipRef.current;
    const shouldSuppressForAnimation = pendingAnimationRef.current && mutationChangedState;
    // Show slash when:
    // - settled skip day with no mutation in progress
    // - unskip mutation in flight (keep slash visible until draw-out plays)
    // - any animation is playing
    const showSlash = isInCurrentMonth &&
        !shouldSuppressForAnimation &&
        ((!!isSkipDay && !isMutating && animationPhase === "idle") ||
            (isMutating && wasSkipRef.current) ||
            animationPhase !== "idle");
    const slashAnimation = animationPhase === "draw-in"
        ? `${slashDrawIn} ${SLASH_ANIMATION_MS}ms ease-out forwards`
        : animationPhase === "draw-out"
            ? `${slashDrawOut} ${SLASH_ANIMATION_MS}ms ease-in forwards`
            : "none";
    return { showSlash, slashAnimation, spinnerColor, wasSkipDay: wasSkipRef.current };
};
